<template>
  <b-modal
    id="page-radio-buttons-modal"
    :ok-title="trans(block && block.id ? 'save' : 'create')"
    :cancel-title="trans('cancel')"
    size="xl"
    no-enforce-focus
    @shown="onShown"
    @hidden="onHidden"
    @ok="onSave"
  >
    <template #modal-title>
      <i class="fa fa-question"></i>
      <span>{{ trans('question') }}</span>
      <small v-if="!!block" class="text-muted ml-2">
        #{{ block.id }}
      </small>
    </template>

    <b-row>
      <b-col class="col-12 col-md-7">
        <b-row>
          <b-col>
            <b-form-group :label="trans('label')">
              <b-form-input size="sm" v-model="label" :state="isLabelValid" />
              <b-form-invalid-feedback v-if="label && label.length > 200" class="text-right">
                200 {{ trans('char_max') }}
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
          <b-col class="col-auto">
            <b-form-group :label="trans('inline')">
              <b-form-checkbox v-model="inline" switch>
                {{ trans(inline ? 'yes' : 'no') }}
              </b-form-checkbox>
            </b-form-group>
          </b-col>
        </b-row>
        <b-form-group>
          <label>
            {{ trans('description') }}
            <b-button
              v-if="!description"
              variant="outline-secondary"
              size="sm"
              class="py-0 ml-2"
              @click="show_description = !show_description"
            >
              <small>
                <i :class="`fa fa-fw fa-eye${show_description ? '-slash' : ''}`"></i>
                {{ trans(show_description ? 'hide' : 'show') }}
              </small>
            </b-button>
          </label>
          <html-textarea
            v-if="show_description || !!description"
            :organization="organization"
            show-insert-form-document
            v-model="description"
          ></html-textarea>
        </b-form-group>

        <div class="my-2">
          <label class="d-block">
            <b-row>
              <b-col>{{ trans('options') }}</b-col>
              <b-col class="col-auto">
                <b-button v-if="!hasDescriptions" variant="link" size="sm" class="p-0 text-muted" @click="show_detail = !show_detail">
                  <i :class="`fa fa-fw fa-eye${show_detail ? '-slash' : ''}`"></i>
                  {{ trans('detail') }}
                </b-button>
                <b-button v-if="!show_detail" variant="link" size="sm" class="p-0 text-muted ml-2" @click="onClickBatchModify">
                  <i class="fa fa-fw fa-edit"></i>
                  {{ trans('batch_modify') }}
                </b-button>
                <textarea-modal
                  id="batch-modify-values-modal"
                  v-model="text_values"
                  @input="onBatchValuesModified"
                  :title="trans('values')"
                  :label="trans('one_value_per_line')"
                ></textarea-modal>
              </b-col>
            </b-row>
          </label>
          <b-form-group v-if="!hasDescriptions && !show_detail" class="mb-1">
            <b-form-tags
              tag-variant="success"
              remove-on-delete
              size="sm"
              v-model="values"
              :placeholder="trans('values')"
              :state="!!values && values.length > 0"
              @input="onChangeValues"
            ></b-form-tags>
          </b-form-group>
          <b-row v-if="hasDescriptions || show_detail" v-for="(o, index) in options" :key="index">
            <b-col class="col-11">
              <b-form-group class="mb-1">
                <b-form-input v-model="o.value" size="sm" @change="onChangeOption" />
              </b-form-group>
              <div v-if="o.description" class="ml-1" style="position: relative;">
                <div v-html="o.description"></div>
                <b-button variant="link" size="sm" class="p-0 text-muted" style="position: absolute; right: 0; bottom: 0;" @click="onClickEditOptionDescription(o, index)">
                  <small>
                    <i class="fa fa-fw fa-edit"></i>
                  </small>
                </b-button>
              </div>
              <div v-if="!o.description" class="text-right ml-1 mb-2">
                <b-button variant="link" size="sm" class="p-0 text-muted" @click="onClickEditOptionDescription(o, index)">
                  <small>
                    <i class="fa fa-fw fa-edit"></i>
                    {{ trans('detail') }}
                  </small>
                </b-button>
              </div>
            </b-col>
            <b-col class="col-1">
              <b-form-group>
                <b-button variant="link" size="sm" class="p-0 text-danger" @click="onClickRemoveOption(index)">
                  <i class="fa fa-fw fa-times"></i>
                </b-button>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col v-if="show_detail || hasDescriptions">
              <b-button variant="outline-primary" size="sm" class="py-0" @click="onClickAddOption">
                <i class="fa fa-fw fa-plus"></i>
                {{ trans('add') }}
              </b-button>
            </b-col>
            <b-col class="col-auto">
              <b-button variant="outline-primary" size="sm" class="py-0" @click="onSortAsc">
                <i class="fa fa-fw fa-sort-alpha-down"></i>
                {{ trans('sort') }}
              </b-button>
              <b-button variant="outline-primary" size="sm" class="py-0" @click="onSortDesc">
                <i class="fa fa-fw fa-sort-alpha-up"></i>
                {{ trans('sort') }}
              </b-button>
            </b-col>
          </b-row>
        </div>
        <br/>

        <b-form-group :label="trans('correct_answer')">
          <b-form-select size="sm" v-model="correct_answer" :options="selectOptions" :disabled="busy"></b-form-select>
        </b-form-group>
        <b-form-group>
          <label>
            {{ trans('correct_answer') }} - {{ trans('description').toLowerCase() }}
            <b-button
              v-if="!correct_answer_description"
              variant="outline-secondary"
              size="sm"
              class="py-0 ml-2"
              @click="show_correct_answer_description = !show_correct_answer_description"
            >
              <small>
                <i :class="`fa fa-fw fa-eye${show_correct_answer_description ? '-slash' : ''}`"></i>
                {{ trans(show_correct_answer_description ? 'hide' : 'show') }}
              </small>
            </b-button>
          </label>
          <html-textarea
            v-if="show_correct_answer_description || !!correct_answer_description"
            :organization="organization"
            show-insert-form-document
            v-model="correct_answer_description"
          ></html-textarea>
        </b-form-group>

      </b-col>

      <b-col class="col-12 col-md-5">
        <label>{{ trans('preview') }}</label>
        <div class="new-form border p-3">
          <page-radio-buttons-block
            v-if="visible"
            :organization="organization"
            :page="page"
            :block="serializedForPreview"
            is-preview
          ></page-radio-buttons-block>
        </div>
      </b-col>
    </b-row>
    <html-textarea-modal
      id="radios-html-textarea-modal"
      :organization="organization"
      v-model="option_description"
      :title="trans('detail')"
      :label="trans('detail')"
      show-insert-form-document
      @input="onOptionDescriptionSaved"
    ></html-textarea-modal>

  </b-modal>
</template>

<script>
  import HtmlTextarea from './../../../common/components/HtmlTextarea'
  import HtmlTextareaModal from './../../../common/components/HtmlTextareaModal'
  import TextareaModal from './../../../common/components/TextareaModal'
  import PageRadioButtonsBlock from './PageRadioButtonsBlock'

  const blankOptions = [
    {
      value: 'Option 1',
      description: null,
    },
    {
      value: 'Option 2',
      description: null,
    },    
  ]

  export default {
    props: {
      organization: Object,
      page: Object,
      block: Object,
      after: Number,
    },
    components: {
      HtmlTextarea,
      HtmlTextareaModal,
      TextareaModal,
      PageRadioButtonsBlock,
    },
    data() {
      return {
        trans: trans,
        busy: false,
        lang: window.lang,
        error: null,
        visible: false,
        label: trans('question'), // text
        description: null, // text2
        show_description: false,
        options: _.clone(blankOptions),
        option_index: null,
        option_description: null,
        show_detail: false,
        inline: false,
        values: _.map(blankOptions, 'value'),
        text_values: null,
        correct_answer: null,
        correct_answer_description: null,
        show_correct_answer_description: false,

        image_percent: 0.3333,
        image_percent_options: [
          {text: '25%', value: 0.25},
          {text: '33%', value: 0.3333},
          {text: '50%', value: 0.5},
          {text: '66%', value: 0.6666},
          {text: '75%', value: 0.75},
        ],
        span_row: false,
        h_align: 'center',
        hAlignOptions: [
          {value: 'left', text: trans('left')},
          {value: 'center', text: trans('center')},
          {value: 'right', text: trans('right')},
        ],
      }
    },
    computed: {
      isLabelValid() {
        return !!this.label && !!this.label.trim() && this.label.length <= 200
      },
      hasDescriptions() {
        return _.reduce(this.options || [], (has, o) => {
          if (!!o.description) has = true
          return has
        }, false)
      },
      selectOptions() {
        return _.map(this.options, o => {
          return {
            value: o.value,
            text: o.value
          }
        })
      },
      serialized() {
        return {
          type: 'radio-buttons',
          text: this.label,
          text2: this.description,
          after: this.after,
          image_percent: this.image_percent,
          meta: {
            options: _.cloneDeep(this.options),
            inline: this.inline,
            correct_answer: this.correct_answer,
            correct_answer_description: this.correct_answer_description,
          },
          span_row: this.span_row,
          h_align: this.h_align,
        }
      },
      serializedForPreview() {
        return _.assign(_.cloneDeep(this.serialized), {span_row: false})
      },
    },
    methods: {
      onShown() {
        this.label = this.block ? this.block.text : trans('question')
        this.description = this.block ? this.block.text2 || null : null
        this.options = this.block ? this.block.meta.options : _.cloneDeep(blankOptions)
        this.values = _.map(this.options, 'value')
        this.inline = this.block ? this.block.meta.inline : false
        this.correct_answer = this.block ? this.block.meta.correct_answer || null : null
        this.correct_answer_description = this.block ? this.block.meta.correct_answer_description || null : null
        this.image_percent = this.block ? this.block.image_percent : 1.0
        this.span_row = this.block ? this.block.span_row || false : false
        this.h_align = this.block ? this.block.h_align || 'center' : 'center'
        this.visible = true
      },
      onHidden() {
        this.visible = false
        this.busy = false
        this.error = null
        this.label = trans('question')
        this.description = null
        this.options = _.cloneDeep(blankOptions)
        this.values = _.map(this.options, 'value')
        this.correct_answer = null
        this.correct_answer_description = null
        this.inline = false
        this.image_percent = 1.0
        this.span_row = false
        this.h_align = 'center'
      },
      onChangeValues() {
        this.options = _.map(this.values, value => {
          return {
            value: value,
            description: null
          }
        })
      },
      onChangeOption() {
        this.values = _.map(this.options, 'value')
        if (this.values.indexOf(this.correct_answer) === -1) {
          this.correct_answer = null
        }
      },
      onClickBatchModify() {
        this.text_values = _.map(this.options, 'value').join('\n')
        this.$bvModal.show('batch-modify-values-modal')
      },
      onBatchValuesModified() {
        this.options = _.map(_.compact(this.text_values.split('\n')), (value, index) => {
          return {
            value: value,
            description: !!this.options[index] ? this.options[index].description || null : null
          }
        })
        this.onChangeOption()
      },
      onSortAsc() {
        this.options = _.sortBy(this.options, (o) => {
          return !!o.value ? o.value.toLowerCase() : o.value
        })
        this.onChangeOption()
      },
      onSortDesc() {
        this.options = _.sortBy(this.options, (o) => {
          return !!o.value ? o.value.toLowerCase() : o.value
        }).reverse()
        this.onChangeOption()
      },
      onClickEditOptionDescription(option, index) {
        this.option_index = index
        this.option_description = option.description
        this.$bvModal.show('radios-html-textarea-modal')
      },
      onOptionDescriptionSaved() {
        let option = this.options[this.option_index]
        option.description = this.option_description
        this.options.splice(this.option_index, 1, option)
      },
      onClickAddOption() {
        this.options.push({
          value: 'Option',
          description: null,
        })
        this.onChangeOption()
      },
      onClickRemoveOption(index) {
        this.options.splice(index, 1)
        this.onChangeOption()
      },
      onSave(e) {
        e.preventDefault()
        this.busy = true
        this.error = null
        let url = `/organization/${this.organization.id}/page/${this.page.id}/block`
        if (this.block && this.block.id) url += `/${this.block.id}`
        axios[this.block && this.block.id ? 'patch' : 'post'](url, this.serialized)
        .then(response => {
          this.busy = false
          this.$emit('saved', response.data)
          this.$bvModal.hide('page-radio-buttons-modal')
        })
        .catch(error => {
          this.busy = false
          this.error = _.errorFromAjax(error)
        })
      },
    },
    mounted() {
      window.pageRadioButtonsModal = this
    },
  }
</script>